import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    TextField,
    Switch,
    FormControlLabel,
    Button,
    Stack,
    Divider,
    Alert,
    CardMedia,
    MenuItem,
    emphasize,
} from '@mui/material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { toast } from 'react-toastify';
import { shopModel } from '../Axios/AxiosConfig';

interface Variant {
    title: string;
    price: string;
}

interface GeneralForm {
    id: string;
    name: string;
    company: string;
    company_id: string;
    producttype: string;
    type: string;
    disease: string;
    qty: string;
    price: string;
    orgprice: string;
    onsale: boolean;
    deliverycharges: string;
    imgurl: string;
    product_image_url: string;
    subtype: string;
    desc: string;
    detaildesc: string;
}


const COMPANY_OPTIONS = [
    { id: '1002', name: 'Indus Biotech', email: 'ayurai@indusbiotech.com' },
    { id: '1003', name: 'BugSpeaks', email: 'hello@bugspeaks.com' },
    { id: '1005', name: 'Maharishi Ayurveda India', email: 'info@maharishiayurvedaindia.com' },
    { id: '1007', name: 'Organic Wellness', email: 'ow@organicwellness.com' },
    { id: '1008', name: 'Microbiome', email: 'info@microbiome.in' },
    { id: '1010', name: 'Trudiagno Chembur Lab', email: 'Chemburlab@trudiagno.com' },
    { id: '1014', name: 'Potion1st', email: "info @potion1st.com" }
];

const quillModules = {
    toolbar: [
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic', 'underline'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['clean'],
    ],
};

const AddProductPage: React.FC = () => {
    const navigate = useNavigate();

    const [general, setGeneral] = useState<GeneralForm>({
        id: '',
        name: '',
        company: '',
        company_id: '',
        producttype: '',
        type: '',
        disease: '',
        qty: '',
        price: '',
        orgprice: '',
        onsale: true,
        deliverycharges: '',
        imgurl: '',
        product_image_url: '',
        subtype: '',
        desc: '',
        detaildesc: '',
    });

    const [html, setHtml] = useState('');
    const [imageUrls, setImageUrls] = useState<string[]>(['']);
    const [error, setError] = useState<string | null>(null);
    const [mainImageFile, setMainImageFile] = useState<File | null>(null);
    const [galleryFiles, setGalleryFiles] = useState<(File | null)[]>([null]);
    const [variants, setVariants] = useState<Variant[]>([]);

    const handleGeneralChange = (field: keyof GeneralForm, value: string | boolean) => {
        setGeneral(prev => {
            const next = {
                ...prev,
                [field]: value,
            };

            // If main price is updated and variants exist, update the first variant's price
            if (field === 'price' && typeof value === 'string' && variants.length > 0) {
                setVariants(prevVars => {
                    const newVars = [...prevVars];
                    newVars[0] = { ...newVars[0], price: value };
                    return newVars;
                });
            }

            return next;
        });
    };

    const handleVariantChange = (index: number, field: keyof Variant, value: string) => {
        setVariants(prev => {
            const next = [...prev];
            next[index] = { ...next[index], [field]: value };

            // If first variant's price is updated, update the main price
            if (index === 0 && field === 'price') {
                setGeneral(prevGen => ({ ...prevGen, price: value }));
            }

            return next;
        });
    };

    const handleAddVariant = () => {
        setVariants(prev => [...prev, { title: '', price: '' }]);
    };

    const handleRemoveVariant = (index: number) => {
        setVariants(prev => prev.filter((_, i) => i !== index));
    };


    const handleImageChange = (index: number, value: string) => {
        setImageUrls(prev => prev.map((url, i) => (i === index ? value : url)));
        setGalleryFiles(prev => prev.map((file, i) => (i === index ? null : file)));
    };

    const handleAddImage = () => {
        setImageUrls(prev => [...prev, '']);
        setGalleryFiles(prev => [...prev, null]);
    };

    const handleRemoveImage = (index: number) => {
        setImageUrls(prev => prev.filter((_, i) => i !== index));
        setGalleryFiles(prev => prev.filter((_, i) => i !== index));
    };

    const uploadImage = async (file: File): Promise<string | null> => {
        try {
            const formData = new FormData();

            formData.append('file', file);

            const response = await shopModel.post('/UploadImage', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            const data: any = response?.data;
            const url: string | undefined =
                data?.url || data?.Location || data?.location || data?.path || data?.imageUrl;

            if (!url) {
                throw new Error('No image URL returned from upload');
            }

            return url;
        } catch (err) {
            console.error('Error uploading image:', err);
            setError('Failed to upload image');
            return null;
        }
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setError(null);

        if (!general.name.trim()) {
            setError('Product name is required');
            return;
        }

        if (!general.price.trim()) {
            setError('Price is required');
            return;
        }

        const formData = new FormData();

        formData.append('id', general.id.trim());

        const generalPayload: any = {
            name: general.name.trim(),
            price: general.price.trim(),
            company: general.company.trim() || undefined,
            company_id: general.company_id.trim() || undefined,
            producttype: general.producttype.trim() || undefined,
            type: general.type.trim() || undefined,
            disease: general.disease.trim() || undefined,
            qty: general.qty.trim() || undefined,
            subtype: general.subtype.trim() || undefined,
            orgprice: general.orgprice.trim() || undefined,
            deliverycharges: general.deliverycharges.trim()
                ? Number(general.deliverycharges.trim())
                : 0,
            desc: general.desc.trim() || undefined,
            detaildesc: general.detaildesc.trim() || undefined,
            imgurl: general.imgurl.trim() || undefined,
            product_image_url: general.product_image_url.trim() || undefined,
            onsale: general.onsale,
            product_id: general.id.trim() || undefined,
        };

        formData.append('general', JSON.stringify(generalPayload));

        if (html.trim()) {
            formData.append('html', html.trim());
        }

        imageUrls
            .map(url => url.trim())
            .filter(url => url.length > 0)
            .forEach(url => {
                formData.append('imageUrls[]', url);
            });

        if (mainImageFile) {
            formData.append('images', mainImageFile);
        }

        galleryFiles.forEach(file => {
            if (file) {
                formData.append('images', file);
            }
        });

        if (variants.length > 0) {
            formData.append('other_info', JSON.stringify({ variants }));
        }


        try {
            const response = await shopModel.post('/CreateProduct', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });


            if (response.status === 201) {

                const created = response?.data;

                const { product, success } = created

                toast.success('Product created successfully');

                if (product?.general) {
                    setGeneral(prev => ({
                        ...prev,
                        imgurl: product.general.imgurl || prev.imgurl,
                    }));
                }

                navigate(`/inventory/${product?.id}`)

                setMainImageFile(null);
                setGalleryFiles([null]);
            }

        } catch (err: any) {
            console.error('Error creating product:', err);
            setError(err?.response?.data?.message || 'Failed to create product');
            return;
        }
    };

    return (
        <Box sx={{ p: 3, pt: 1.5, minHeight: '100vh' }}>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                    <Typography
                        variant="h4"
                        component="h1"
                        sx={{ fontWeight: 600, fontFamily: 'Libre Baskerville', color: '#3B362A' }}
                    >
                        Add Product
                    </Typography>
                    <Typography variant="subtitle1" color="text.secondary">
                        Create a new product with full details
                    </Typography>
                </Box>
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => navigate(-1)}
                    sx={{ textTransform: 'none', borderRadius: 2 }}
                >
                    Back
                </Button>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            <form onSubmit={handleSubmit}>
                <Grid container spacing={4}>
                    <Grid item xs={12} md={7}>
                        <Card sx={{ borderRadius: 3, boxShadow: '0 6px 24px rgba(0,0,0,0.12)' }}>
                            <CardContent sx={{ p: 3 }}>
                                <Grid container spacing={2} sx={{ mb: 2 }}>

                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Name"
                                            fullWidth
                                            size="small"
                                            required
                                            value={general.name}
                                            onChange={(e) => handleGeneralChange('name', e.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            select
                                            label="Company"
                                            fullWidth
                                            size="small"
                                            value={general.company_id}
                                            onChange={(e) => {
                                                const id = e.target.value;
                                                const option = COMPANY_OPTIONS.find(o => o.id === id);
                                                setGeneral(prev => ({
                                                    ...prev,
                                                    company_id: id,
                                                    company: option?.name || prev.company,
                                                }));
                                            }}
                                        >
                                            <MenuItem value="">
                                                <em>None</em>
                                            </MenuItem>
                                            {COMPANY_OPTIONS.map(option => (
                                                <MenuItem key={option.id} value={option.id}>
                                                    {option.name}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Product Type"
                                            fullWidth
                                            size="small"
                                            value={general.producttype}
                                            onChange={(e) => handleGeneralChange('producttype', e.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Category"
                                            fullWidth
                                            size="small"
                                            value={general.type}
                                            onChange={(e) => handleGeneralChange('type', e.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Disease"
                                            fullWidth
                                            size="small"
                                            value={general.disease}
                                            onChange={(e) => handleGeneralChange('disease', e.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Pack Size (qty)"
                                            fullWidth
                                            size="small"
                                            value={general.qty}
                                            onChange={(e) => handleGeneralChange('qty', e.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Tags (subtype)"
                                            fullWidth
                                            size="small"
                                            placeholder="Comma-separated tags"
                                            value={general.subtype}
                                            onChange={(e) => handleGeneralChange('subtype', e.target.value)}
                                        />
                                    </Grid>
                                </Grid>

                                <Divider sx={{ my: 2 }} />

                                <Grid container spacing={2} sx={{ mb: 2 }}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Price (₹)"
                                            fullWidth
                                            size="small"
                                            required
                                            type="number"
                                            value={general.price}
                                            onChange={(e) => handleGeneralChange('price', e.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="MRP (Org Price)"
                                            fullWidth
                                            size="small"
                                            type="number"
                                            value={general.orgprice}
                                            onChange={(e) => handleGeneralChange('orgprice', e.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Delivery Charges"
                                            fullWidth
                                            size="small"
                                            type="number"
                                            value={general.deliverycharges}
                                            onChange={(e) => handleGeneralChange('deliverycharges', e.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={general.onsale}
                                                    onChange={(_, checked) => handleGeneralChange('onsale', checked)}
                                                    size="small"
                                                />
                                            }
                                            label="In Stock"
                                        />
                                    </Grid>
                                </Grid>

                                <Divider sx={{ my: 2 }} />

                                <Box sx={{ mt: 1 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                                        Short Description
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        multiline
                                        minRows={2}
                                        size="small"
                                        value={general.desc}
                                        onChange={(e) => handleGeneralChange('desc', e.target.value)}
                                    />
                                </Box>

                                <Box sx={{ mt: 2 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                                        Detailed Description
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        multiline
                                        minRows={3}
                                        size="small"
                                        value={general.detaildesc}
                                        onChange={(e) => handleGeneralChange('detaildesc', e.target.value)}
                                    />
                                </Box>

                                <Box sx={{ mt: 2 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                                        HTML Content
                                    </Typography>
                                    <ReactQuill
                                        theme="snow"
                                        value={html}
                                        onChange={(value: string) => setHtml(value)}
                                        modules={quillModules}
                                        style={{ height: 220, marginTop: 8 }}
                                    />
                                </Box>

                                <Divider sx={{ my: 4 }} />

                                <Box sx={{ mt: 2 }}>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                            Variants (Pricing)
                                        </Typography>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            onClick={handleAddVariant}
                                            sx={{ textTransform: 'none' }}
                                        >
                                            Add Variant
                                        </Button>
                                    </Stack>
                                    {variants.map((v, index) => (
                                        <Grid container spacing={2} key={index} sx={{ mb: 1, alignItems: 'center' }}>
                                            <Grid item xs={5}>
                                                <TextField
                                                    label="Title (e.g. 400 GM)"
                                                    fullWidth
                                                    size="small"
                                                    value={v.title}
                                                    onChange={(e) => handleVariantChange(index, 'title', e.target.value)}
                                                />
                                            </Grid>
                                            <Grid item xs={5}>
                                                <TextField
                                                    label="Price (₹)"
                                                    fullWidth
                                                    size="small"
                                                    type="number"
                                                    value={v.price}
                                                    onChange={(e) => handleVariantChange(index, 'price', e.target.value)}
                                                />
                                            </Grid>
                                            <Grid item xs={2}>
                                                <Button
                                                    color="error"
                                                    onClick={() => handleRemoveVariant(index)}
                                                >
                                                    Remove
                                                </Button>
                                            </Grid>
                                        </Grid>
                                    ))}
                                    {variants.length === 0 && (
                                        <Typography variant="body2" color="text.secondary">
                                            No variants added. The main price will be used.
                                        </Typography>
                                    )}
                                </Box>
                            </CardContent>

                        </Card>
                    </Grid>

                    <Grid item xs={12} md={5}>
                        <Card sx={{ borderRadius: 3, boxShadow: '0 6px 24px rgba(0,0,0,0.12)' }}>
                            <CardContent sx={{ p: 3 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                                    Images
                                </Typography>
                                <Stack spacing={1.5}>
                                    <TextField
                                        label="Main Image URL (imgurl)"
                                        fullWidth
                                        size="small"
                                        value={general.imgurl}
                                        onChange={(e) => handleGeneralChange('imgurl', e.target.value)}
                                    />
                                    {(mainImageFile || general.imgurl) && (
                                        <Box sx={{ mt: 0.5, display: 'flex', justifyContent: 'center' }}>
                                            <CardMedia
                                                component="img"
                                                image={
                                                    mainImageFile
                                                        ? URL.createObjectURL(mainImageFile)
                                                        : general.imgurl
                                                }
                                                alt={general.name || 'Main image preview'}
                                                sx={{
                                                    maxHeight: 150,
                                                    width: 'auto',
                                                    objectFit: 'contain',
                                                    borderRadius: 2,
                                                    border: '1px solid',
                                                    borderColor: 'divider',
                                                }}
                                            />
                                        </Box>
                                    )}
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        component="label"
                                        sx={{ textTransform: 'none', alignSelf: 'flex-start' }}
                                    >
                                        Upload Main Image
                                        <input
                                            type="file"
                                            accept="image/*"
                                            hidden
                                            onChange={(e) => {
                                                const file = e.target.files?.[0];
                                                if (!file) return;
                                                setMainImageFile(file);
                                                e.target.value = '';
                                            }}
                                        />
                                    </Button>
                                    <TextField
                                        label="Alternate Image URL (product_image_url)"
                                        fullWidth
                                        size="small"
                                        value={general.product_image_url}
                                        onChange={(e) => handleGeneralChange('product_image_url', e.target.value)}
                                    />

                                    <Divider sx={{ my: 1 }} />

                                    <Typography variant="body2" color="text.secondary">
                                        Additional gallery images (images array)
                                    </Typography>
                                    {imageUrls.map((url, index) => (
                                        <Box key={index}>
                                            <Stack
                                                direction="row"
                                                spacing={1}
                                                alignItems="center"
                                                sx={{ mb: 0.5 }}
                                            >
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    label={`Image ${index + 1} URL`}
                                                    value={url}
                                                    onChange={(e) => handleImageChange(index, e.target.value)}
                                                />
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    component="label"
                                                    sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                                                >
                                                    Upload
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        hidden
                                                        onChange={(e) => {
                                                            const file = e.target.files?.[0];
                                                            if (!file) return;
                                                            setGalleryFiles(prev => {
                                                                const next = [...prev];
                                                                next[index] = file;
                                                                return next;
                                                            });
                                                            e.target.value = '';
                                                        }}
                                                    />
                                                </Button>
                                                <Button
                                                    variant="outlined"
                                                    color="error"
                                                    size="small"
                                                    onClick={() => handleRemoveImage(index)}
                                                    sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                                                >
                                                    Remove
                                                </Button>
                                            </Stack>
                                            {(galleryFiles[index] || url) && (
                                                <Box
                                                    sx={{
                                                        mt: 0.5,
                                                        display: 'inline-block',
                                                        borderRadius: 1,
                                                        border: '1px solid',
                                                        borderColor: 'divider',
                                                        overflow: 'hidden',
                                                    }}
                                                >
                                                    <CardMedia
                                                        component="img"
                                                        image={
                                                            galleryFiles[index]
                                                                ? URL.createObjectURL(galleryFiles[index] as File)
                                                                : url
                                                        }
                                                        alt={`Image ${index + 1} preview`}
                                                        sx={{
                                                            width: 72,
                                                            height: 72,
                                                            objectFit: 'cover',
                                                        }}
                                                    />
                                                </Box>
                                            )}
                                        </Box>
                                    ))}
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={handleAddImage}
                                        sx={{ textTransform: 'none', alignSelf: 'flex-start' }}
                                    >
                                        Add Image
                                    </Button>
                                </Stack>
                            </CardContent>
                        </Card>

                        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                sx={{ textTransform: 'none', borderRadius: 2 }}
                            >
                                Save Product
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </form>
        </Box>
    );
};

export default AddProductPage;
